import React, { useEffect, useState } from 'react';
import { Loader2, Phone, Plus, Save, X } from 'lucide-react';

import { ttsConfigAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from '../ui/use-toast';

// 2026-08-12: WhatsApp Calling's TTS provider config, per-account —
// mirrors DigitalCompanionSettings.tsx's shape (load-on-mount, edit local
// state, PUT the whole thing on Save) but far smaller in scope: this is
// one provider choice + a few provider-specific fields, not a multi-tab
// product configuration. Backend: app/api/tts_config.py,
// app/models/tts_config.py (AccountTTSConfig). Supersedes the original
// "global env-only" first cut (settings.TTS_PROVIDER in .env) — see
// Docs/CALLING_CONFIGURABLE_TTS_AND_TICKETING_COUNSELLING_PLAN.md Part A.
//
// HONEST LIMITATION carried over from the backend: the calling pipeline's
// cached ack/greeting clips (process-lifetime, shared across every
// account) still always use the global env default regardless of what's
// saved here — only live per-turn answers (and the greeting's live
// fallback, when its cache isn't warm yet) actually read this config. See
// whatsapp_call_session.py's _get_ack_pcm_cache/_get_greeting_pcm_cache
// comments for the full reasoning.

type TTSConfig = {
  enabled: boolean;
  provider: string;
  fallback_provider: string;
  elevenlabs_api_key?: string | null; // masked when returned from GET
  elevenlabs_voice_id?: string | null;
  elevenlabs_model_id?: string | null;
  elevenlabs_output_format?: string | null;
  edge_tts_voice?: string | null;
  has_elevenlabs_key?: boolean;
  // 2026-08-16: Digital Companion's own voice override — same provider as
  // above, different voice ID. Blank = Digital Companion uses the regular
  // voice too, unchanged from before this existed.
  digital_companion_voice_id?: string | null;
  // 2026-08-27 (Docs/DIGITAL_COMPANION_MULTILINGUAL_RESPONSES_PLAN.md Phase
  // 3): per-language voice overrides — a language with no entry here falls
  // back to edge_tts_voice/elevenlabs_voice_id above, exactly as before
  // this existed. 2026-09-25: except for edge-tts, where a voice for the
  // wrong language is swapped for a built-in one for the caller's language
  // (backend app/config/calling_defaults.py) instead of mispronouncing it.
  edge_tts_voice_by_language?: Record<string, string> | null;
  elevenlabs_voice_id_by_language?: Record<string, string> | null;
  // 2026-09-25 (Docs/CALLING_LANGUAGE_AND_FRESH_CALL_ISSUES_PLAN.md): which
  // ElevenLabs key calls actually use — one key covers TTS and STT, so a
  // key saved on the Speech-to-Text card counts here too.
  elevenlabs_key_source?: 'account_tts_config' | 'account_calling_stt_config' | 'env' | 'none';
};

function elevenLabsKeyHint(config: TTSConfig): string {
  if (config.has_elevenlabs_key) return 'A key is already configured — leave blank to keep it.';
  if (config.elevenlabs_key_source === 'account_calling_stt_config') {
    return 'Using the key saved on the Speech-to-Text card — leave blank to keep using it.';
  }
  if (config.elevenlabs_key_source === 'env') {
    return "Using the deployment's ElevenLabs key — leave blank to keep using it.";
  }
  return 'No key configured yet — enter one here or on the Speech-to-Text card.';
}

type VoiceOption = { id: string; label: string };
type CuratedVoices = Record<string, VoiceOption[]>;
type LanguageOption = { code: string; label: string };
type LanguageVoiceRow = { code: string; edgeVoice: string; elevenVoice: string };

const CUSTOM_VOICE_VALUE = '__custom__';

const PROVIDERS = [
  { value: 'edge_tts', label: 'Edge TTS (free, default)' },
  { value: 'chatterbox', label: 'Chatterbox (self-hosted GPU)' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
];

const DEFAULT_CONFIG: TTSConfig = {
  enabled: true,
  provider: 'edge_tts',
  fallback_provider: 'edge_tts',
  elevenlabs_api_key: '',
  elevenlabs_voice_id: '',
  elevenlabs_model_id: 'eleven_turbo_v2_5',
  elevenlabs_output_format: 'pcm_16000',
  edge_tts_voice: 'en-KE-AsiliaNeural',
  digital_companion_voice_id: '',
};

export default function CallingVoiceSettings() {
  const [config, setConfig] = useState<TTSConfig>(DEFAULT_CONFIG);
  const [newElevenLabsKey, setNewElevenLabsKey] = useState('');
  const [voices, setVoices] = useState<CuratedVoices>({ edge_tts: [], elevenlabs: [], chatterbox: [] });
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [langRows, setLangRows] = useState<LanguageVoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, voiceData, languageData] = await Promise.all([
          ttsConfigAPI.getConfig(),
          ttsConfigAPI.getVoices().catch(() => ({})), // curated list is a nice-to-have — don't fail the page over it
          ttsConfigAPI.getLanguages().catch(() => []), // same — don't fail the page over this section either
        ]);
        if (!cancelled) {
          setConfig({ ...DEFAULT_CONFIG, ...data });
          setVoices({ edge_tts: [], elevenlabs: [], chatterbox: [], ...voiceData });
          setLanguages(Array.isArray(languageData) ? languageData : []);
          // 2026-08-27: union of both maps' keys — a language could have
          // only an edge-tts entry, only an ElevenLabs entry, or both; one
          // row still represents it either way.
          const edgeMap = data.edge_tts_voice_by_language || {};
          const elevenMap = data.elevenlabs_voice_id_by_language || {};
          const codes = Array.from(new Set([...Object.keys(edgeMap), ...Object.keys(elevenMap)]));
          setLangRows(
            codes.map((code) => ({
              code,
              edgeVoice: edgeMap[code] || '',
              elevenVoice: elevenMap[code] || '',
            }))
          );
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load calling voice settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const edgeVoices = voices.edge_tts || [];
  const isCustomEdgeVoice =
    !!config.edge_tts_voice && !edgeVoices.some((v) => v.id === config.edge_tts_voice);
  const [edgeVoiceMode, setEdgeVoiceMode] = useState<'preset' | 'custom'>('preset');
  useEffect(() => {
    if (isCustomEdgeVoice) setEdgeVoiceMode('custom');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // only re-derive once, right after config first loads

  const addLanguageRow = () => {
    const used = new Set(langRows.map((r) => r.code));
    const next = languages.find((l) => !used.has(l.code)) || languages[0];
    if (!next) return;
    setLangRows((rows) => [...rows, { code: next.code, edgeVoice: '', elevenVoice: '' }]);
  };

  const updateLanguageRow = (index: number, patch: Partial<LanguageVoiceRow>) => {
    setLangRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeLanguageRow = (index: number) => {
    setLangRows((rows) => rows.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const edgeByLanguage: Record<string, string> = {};
      const elevenByLanguage: Record<string, string> = {};
      for (const row of langRows) {
        if (!row.code) continue;
        if (row.edgeVoice) edgeByLanguage[row.code] = row.edgeVoice;
        if (row.elevenVoice) elevenByLanguage[row.code] = row.elevenVoice;
      }
      const payload = {
        enabled: config.enabled,
        provider: config.provider,
        fallback_provider: config.fallback_provider || 'edge_tts',
        // Only send a new key if the admin actually typed one — never
        // resend the masked placeholder we got back from GET.
        elevenlabs_api_key: newElevenLabsKey || undefined,
        elevenlabs_voice_id: config.elevenlabs_voice_id || undefined,
        elevenlabs_model_id: config.elevenlabs_model_id || undefined,
        elevenlabs_output_format: config.elevenlabs_output_format || undefined,
        edge_tts_voice: config.edge_tts_voice || undefined,
        digital_companion_voice_id: config.digital_companion_voice_id || undefined,
        edge_tts_voice_by_language: edgeByLanguage,
        elevenlabs_voice_id_by_language: elevenByLanguage,
      };
      const updated = await ttsConfigAPI.updateConfig(payload);
      setConfig({ ...DEFAULT_CONFIG, ...updated });
      setNewElevenLabsKey('');
      toast({ title: 'Saved', description: 'Calling voice settings updated.' });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save settings';
      setError(typeof msg === 'string' ? msg : 'Failed to save settings');
      toast({ title: 'Save failed', description: String(msg), variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading calling voice settings…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Phone className="h-5 w-5" />
              WhatsApp Calling — Voice Provider
            </CardTitle>
            <CardDescription className="mt-1">
              Choose which text-to-speech engine Maya uses on live WhatsApp calls for this account.
              Falls back automatically if the chosen provider fails or isn't configured.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="tts-enabled" className="text-sm text-muted-foreground">
              Enabled
            </Label>
            <Switch
              id="tts-enabled"
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig((c) => ({ ...c, enabled: checked }))}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select
                value={config.provider}
                onValueChange={(v) => setConfig((c) => ({ ...c, provider: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fallback provider</Label>
              <Select
                value={config.fallback_provider}
                onValueChange={(v) => setConfig((c) => ({ ...c, fallback_provider: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a fallback" />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Used if the primary provider fails. Edge TTS is always tried last, regardless of this setting.
              </p>
            </div>
          </div>

          {config.provider === 'chatterbox' && (
            <Alert>
              <AlertDescription>
                Chatterbox is currently disabled deployment-wide due to an unresolved barge-in/GPU-queue
                issue (see the calling plan doc). Selecting it will silently fall back to the fallback
                provider until that's fixed.
              </AlertDescription>
            </Alert>
          )}

          {config.provider === 'elevenlabs' && (
            <div className="space-y-4 rounded-xl border border-border/60 bg-muted/20 p-4">
              <p className="text-sm font-medium">ElevenLabs settings</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="el-api-key">API key</Label>
                  <Input
                    id="el-api-key"
                    type="password"
                    placeholder={
                      config.has_elevenlabs_key
                        ? config.elevenlabs_api_key || '••••••••'
                        : config.elevenlabs_key_source && config.elevenlabs_key_source !== 'none'
                          ? '••••••••'
                          : 'sk-...'
                    }
                    value={newElevenLabsKey}
                    onChange={(e) => setNewElevenLabsKey(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{elevenLabsKeyHint(config)}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="el-voice-id">Voice ID</Label>
                  <Input
                    id="el-voice-id"
                    placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
                    value={config.elevenlabs_voice_id || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, elevenlabs_voice_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Curated voice presets aren't available for ElevenLabs yet — paste a voice ID from
                    your ElevenLabs account for now. A picker like Edge TTS's below is planned once a
                    key is configured here.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="el-model-id">Model</Label>
                  <Input
                    id="el-model-id"
                    placeholder="eleven_turbo_v2_5"
                    value={config.elevenlabs_model_id || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, elevenlabs_model_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Low-latency models (e.g. eleven_turbo_v2_5) matter for a live call.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="el-output-format">Output format</Label>
                  <Input
                    id="el-output-format"
                    placeholder="pcm_16000"
                    value={config.elevenlabs_output_format || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, elevenlabs_output_format: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          {config.provider === 'edge_tts' && (
            <div className="space-y-2">
              <Label htmlFor="edge-voice">Edge TTS voice</Label>
              <Select
                value={edgeVoiceMode === 'custom' ? CUSTOM_VOICE_VALUE : config.edge_tts_voice || ''}
                onValueChange={(v) => {
                  if (v === CUSTOM_VOICE_VALUE) {
                    setEdgeVoiceMode('custom');
                  } else {
                    setEdgeVoiceMode('preset');
                    setConfig((c) => ({ ...c, edge_tts_voice: v }));
                  }
                }}
              >
                <SelectTrigger id="edge-voice">
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
                <SelectContent>
                  {edgeVoices.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.label}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_VOICE_VALUE}>Custom (enter voice ID)…</SelectItem>
                </SelectContent>
              </Select>
              {edgeVoiceMode === 'custom' && (
                <Input
                  className="mt-2"
                  placeholder="e.g. en-US-JennyNeural — any Microsoft edge-tts voice ID"
                  value={config.edge_tts_voice || ''}
                  onChange={(e) => setConfig((c) => ({ ...c, edge_tts_voice: e.target.value }))}
                />
              )}
              <p className="text-xs text-muted-foreground">
                Presets are Microsoft's African-English neural voices, verified against the live
                edge-tts voice list. Pick "Custom" to use any other edge-tts voice ID by name.
              </p>
            </div>
          )}

          <div className="space-y-2 rounded-xl border border-border/60 bg-muted/20 p-4">
            <Label htmlFor="dc-voice-id">Digital Companion voice ID (optional)</Label>
            <Input
              id="dc-voice-id"
              placeholder={
                config.provider === 'elevenlabs'
                  ? 'An ElevenLabs voice ID, e.g. 21m00Tcm4TlvDq8ikWAM'
                  : 'An edge-tts voice ID, e.g. en-NG-EzinneNeural'
              }
              value={config.digital_companion_voice_id || ''}
              onChange={(e) => setConfig((c) => ({ ...c, digital_companion_voice_id: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Gives Digital Companion (ticket intake, status checks, counselling) its own voice on
              calls, separate from Maya's regular Q&A voice above — same provider, a different
              voice ID. Leave blank to use the regular voice for Digital Companion too.
            </p>
          </div>

          <div className="flex justify-end border-t border-border/60 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2026-08-27 (Docs/DIGITAL_COMPANION_MULTILINGUAL_RESPONSES_PLAN.md
          Phase 3): per-language voice overrides. A contact's language
          comes from the welcome-message language-picker buttons (Inbox
          settings) or auto-detection -- this just lets an admin pick which
          voice speaks each language, instead of the account's one regular
          voice speaking every language. No entry for a language = that
          language still uses the regular voice above. Its own card (was a
          sub-section of the card above) so it reads as a distinct,
          optional extra rather than more of the same form. */}
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">Per-language voices</CardTitle>
            <CardDescription>
              Optional — a language with no row here keeps using the regular ElevenLabs voice above.
              For Edge TTS, a caller whose language doesn't match the regular voice gets a built-in
              voice for their language instead (e.g. French: Denise).
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addLanguageRow}
            disabled={languages.length === 0 || langRows.length >= languages.length}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add language
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-3">
            {langRows.length === 0 && (
              <p className="text-xs text-muted-foreground">No per-language voices configured yet.</p>
            )}

            {langRows.map((row, index) => {
              const usedByOtherRows = new Set(
                langRows.filter((_, i) => i !== index).map((r) => r.code)
              );
              return (
                <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,140px)_1fr_1fr_auto] items-start">
                  <Select
                    value={row.code}
                    onValueChange={(v) => updateLanguageRow(index, { code: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages
                        .filter((l) => l.code === row.code || !usedByOtherRows.has(l.code))
                        .map((l) => (
                          <SelectItem key={l.code} value={l.code}>
                            {l.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={row.edgeVoice || '__none__'}
                    onValueChange={(v) => updateLanguageRow(index, { edgeVoice: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Edge TTS voice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {edgeVoices.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="ElevenLabs voice ID (optional)"
                    value={row.elevenVoice}
                    onChange={(e) => updateLanguageRow(index, { elevenVoice: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLanguageRow(index)}
                    aria-label="Remove language"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end border-t border-border/60 pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
